import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { TenantContext } from '../context/TenantContext';
import { canAccessPoolWorkflow } from '../utils/workflowGuards';
import { getTenantLabels } from '../utils/tenantTerms';
import './Sidebar.css';
import { getTenantFromSubdomain } from '../utils/tenant';
import API from '../api/axios';
import { Icon } from './SvgIcons';

const Sidebar = () => {
  const { user } = useContext(AuthContext);
  const { tenantType } = useContext(TenantContext);
  const labels = getTenantLabels(tenantType);
  const location = useLocation();
  const [tenantInfo, setTenantInfo] = useState(null);
  // Don't show sidebar on auth pages
  const authPages = ['/login', '/register'];
  // Load tenant details if a subdomain is active
  useEffect(() => {
    const fetchTenantDetails = async () => {
      const slug = getTenantFromSubdomain();
      // if (!slug) return;

      if (slug) {
        try {
          const res = await API.get(`/public/tenant-details?slug=${slug}`);
          if (res.data.success) {
            setTenantInfo(res.data.tenant);
          }
        } catch (err) {
          console.error('Failed to load tenant details:', err);
        }
      }
    };

    fetchTenantDetails();
  }, []);

  if (!user || authPages.includes(location.pathname)) return null;

  const isActive = (path) => location.pathname === path;


  // Role-based menu items
  const getMenuItems = () => {
    const commonItems = [
      { path: '/profile', icon: 'User', label: 'Profile' },
    ];

    switch (user.role) {
      case 'super_admin':
        return [
          { path: '/super-admin', icon: 'Home', label: 'Dashboard' },
          { path: '/super-admin/tenants', icon: 'Building', label: 'Tenants' },
          { path: '/tenant-management', icon: 'Settings', label: 'Tenant Management' },
          { path: '/super-admin/user-management', icon: 'Users', label: 'User Management' },
          { path: '/super-admin/finalized-papers', icon: 'FileText', label: 'Finalized Papers' },
          { path: '/super-admin/token-usage', icon: 'ChartBar', label: 'Token Usage' },
          { path: '/super-admin/settings', icon: 'Settings', label: 'Settings' },
        ];

      case 'university_admin':
        return [
          { path: '/university-admin', icon: 'Home', label: 'Admin Dashboard' },
          { path: '/university-admin/templates', icon: 'Clipboard', label: 'Templates' },
          { path: '/university-admin/finalized-papers', icon: 'FileText', label: 'Finalized Papers' },
          { path: '/university-admin/faculty-certification', icon: 'GraduationCap', label: 'Faculty Certification' },
          { path: '/university-admin/college-management', icon: 'Building', label: 'University Management' },
          { path: '/university-admin/user-management', icon: 'Users', label: 'User Management' },
          { path: '/university-admin/subject-management', icon: 'Book', label: 'Subject Management' },
          ...commonItems,
        ];

      case 'coe':
        return [
          { path: '/coe-dashboard', icon: 'Home', label: 'COE Dashboard' },
          { path: '/coe/my-pool-papers', icon: 'FileText', label: 'My Pool Papers' },
          { path: '/coe/templates', icon: 'Clipboard', label: 'Templates' },
          { path: '/university-pool/coe', icon: 'Scale', label: 'COE Review' },
          ...commonItems,
        ];

      case 'college_admin':
        return [
          { path: '/college-admin', icon: 'Home', label: 'Dashboard' },
          { path: '/college-admin/departments', icon: 'Building', label: 'Departments' },
          { path: '/college-admin/subjects', icon: 'Book', label: 'Subject Management' },
          { path: '/college-admin/users', icon: 'Users', label: 'User Management' },
          { path: '/college-admin/templates', icon: 'Clipboard', label: 'Templates' },
          { path: '/college-admin/finalized-papers', icon: 'FileText', label: 'Finalized Papers' },
          ...commonItems,
        ];

      case 'hod':
        return [
          { path: '/hod', icon: 'Home', label: 'Dashboard' },
          { path: '/hod/subjects', icon: 'Book', label: 'Subjects' },
          { path: '/hod/user-management', icon: 'Users', label: 'User Management' },
          { path: '/hod/templates', icon: 'Clipboard', label: 'Templates' },
          { path: '/hod/finalized-papers', icon: 'FileText', label: 'Finalized Papers' },
          ...commonItems,
        ];

      case 'moderator':
        const moderatorItems = [
          { path: '/moderator', icon: 'Home', label: 'Dashboard', workflow: 'college' },
          { path: '/moderator-categorization', icon: 'Target', label: 'Categorize Papers', workflow: 'college' },
          { path: '/moderator/users', icon: 'Users', label: 'Users', workflow: 'college' },
          { path: '/moderator/approvals', icon: 'CheckCircle', label: 'Approvals', workflow: 'college' },
        ];

        // Add university pool access for certified moderators
        if (canAccessPoolWorkflow(user)) {
          moderatorItems.splice(2, 0, {
            path: '/university-pool/moderator',
            icon: 'Globe',
            label: 'University Pool',
            badge: 'Certified',
            workflow: 'pool'
          });
        }

        return [...moderatorItems, ...commonItems];

      case 'coordinator':
        return [
          { path: '/coordinator', icon: 'Home', label: 'Dashboard' },
          { path: '/coordinator/papers', icon: 'FileText', label: 'Question Papers' },
          { path: '/coordinator/review', icon: 'Search', label: 'Review Papers' },
          { path: '/coordinator/faculty', icon: 'Examiner', label: 'Faculty' },
          { path: '/coordinator/reports', icon: 'ChartBar', label: 'Reports' },
          ...commonItems,
        ];

      case 'subject_matter_expert':
        const smeItems = [
          { path: '/subject-matter-expert', icon: 'Home', label: 'Dashboard', workflow: 'college' },
          { path: '/sme-papers', icon: 'Clipboard', label: 'Review Papers', workflow: 'college' },
          { path: '/my-examiners', icon: 'Examiner', label: 'My Examiners', workflow: 'college' },
        ];

        // Add university pool access for certified SMEs
        if (canAccessPoolWorkflow(user)) {
          smeItems.splice(2, 0, {
            path: '/university-pool/sme',
            icon: 'Globe',
            label: 'University Pool',
            badge: 'Certified',
            workflow: 'pool'
          });
        }

        return [...smeItems, ...commonItems];

      case 'examiner':
        const examinerItems = [
          { path: '/examiner', icon: 'Home', label: 'Dashboard', workflow: 'college' },
          { path: '/examiner/create', icon: 'Sparkles', label: 'Generate Questions', workflow: 'college' },
          { path: '/examiner/papers', icon: 'FileText', label: 'My Papers', workflow: 'college' },
          { path: '/paper-templates', icon: 'Clipboard', label: 'Paper Templates', workflow: 'college' },
        ];

        // Add university pool access for certified examiners
        if (canAccessPoolWorkflow(user)) {
          examinerItems.splice(3, 0, {
            path: '/university-pool/examiner',
            icon: 'Globe',
            label: 'University Pool',
            badge: 'Certified'
          });
        }

        return [...examinerItems, ...commonItems];

      default:
        return commonItems;
    }
  };

  const menuItems = getMenuItems();

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <div className="sidebar-brand">
          <span className="brand-icon">{tenantInfo?.logo_url ? <img
            src={process.env.REACT_APP_DEV_API_URL ? `${process.env.REACT_APP_DEV_API_URL}${tenantInfo.logo_url}` : `http://localhost:5000${tenantInfo.logo_url}`}
            alt={tenantInfo.university_name}
            style={{ height: '30px', maxWidth: '80px', objectFit: 'contain' }}
          /> : <Icon name="FileText" size={24} />}</span>
          <span className="brand-text">{tenantInfo?.university_name ? tenantInfo.university_name : labels.tenantLabel}</span>
        </div>
      </div>

      <nav className="sidebar-nav">
        {menuItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`sidebar-link ${isActive(item.path) ? 'active' : ''} ${item.workflow === 'pool' ? 'pool-workflow' : ''}`}
          >
            <span className="sidebar-icon"><Icon name={item.icon} size={18} /></span>
            <span className="sidebar-label">
              {item.label}
              {item.badge && (
                <span className="sidebar-badge">{item.badge}</span>
              )}
            </span>
          </Link>
        ))}
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-user">
          <div className="user-avatar">
            {user.name.charAt(0).toUpperCase()}
          </div>
          <div className="user-info">
            <div className="user-name">{user.name}</div>
            <div className="user-role">{labels[user.role] || user.role.replace('_', ' ')}</div>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;